import { FIREBASE_EVENT_NAMES, FIREBASE_EVENT_PARAMS, PAGES_ROUTES, PAGES_TITLE } from "utils/Constants";
import { Image, StyleSheet, View } from "react-native";
import React, { useCallback, useEffect, useState } from "react";
import { hp, spH, spV, wp } from "utils/normalizeV2";
import { useDispatch, useSelector } from "react-redux";
import { useLocalSearchParams, useRouter } from "expo-router";

import { ActivityIndicator } from "react-native-web";
import Buttons from "components/Buttons";
import { Colors } from "colors";
import { Images } from "assets";
import { Svg as Svgs } from "assets/svg";
import ListView from "components/ListView";
import ParentScrollView from "components/ParentScrollView";
import ProductCard from "components/PLP/ProductCard";
import SideProfileComp from "components/profile/SideProfileComp";
import VText from "components/VText";
import { analytics } from "utils/firebaseConfig";
import { fp } from "utils/normalizeV2";
import { trackV2 } from "utils/Segment";
import { ANALYTICS_EVENT_NAMES } from "utils/analytics";
import EmptyScreen from "components/emptyScreen";
import { getFavProducts } from "appRedux/reducers/productSlice";

const Favorites = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [favList, setFavList] = useState([]);
    const [error, setError] = useState(null);

    const userId = useSelector((state) => state.AuthReducer.userId);

    const router = useRouter();
    const dispatch = useDispatch();
    const local = useLocalSearchParams();
    const gender = local?.gender || "female";

    useEffect(() => {
        const eventData = {
            favorites_count: favList?.length,
        };
        trackV2(ANALYTICS_EVENT_NAMES.FAVORITES_PAGE_VIEWED, eventData);
    }, [favList]);

    const getFavorites = useCallback(
        (showLoader = true) => {
            if (showLoader) {
                setIsLoading(true);
            }
            setError(null);
            dispatch(getFavProducts())
                .then((res) => {
                    const resData = res?.payload?.data?.data;
                    if (resData && Array.isArray(resData?.productDetails)) {
                        setFavList(resData.productDetails);
                    } else {
                        setError("No favorites found");
                    }
                })
                .catch((err) => {
                    setError("Failed to fetch favorites");
                    console.error("Error fetching favorites:", err);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
        [dispatch, userId]
    );

    useEffect(() => {
        getFavorites();
    }, [getFavorites]);

    const renderContent = useCallback(() => {
        if (isLoading) {
            return (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color={Colors.black} />
                </View>
            );
        }

        if (error) {
            return <VText style={styles.errorText}>{error}</VText>;
        }

        if (favList.length === 0) {
            return (
                <EmptyScreen
                    icon={Svgs.favIcons}
                    title="You have no favorite items saved... yet!"
                    description="Please click the heart ico n on any product to save it here and refer to later."
                    buttonText="Browse Catalog"
                    onPress={() =>
                        router.push("/shop/all-new-arrivals?optionId=1&title=All+New+Arrivals&gender=female")
                    }
                />
            );
        }

        return (
            <ListView
                style={styles.listView}
                data={favList}
                estimatedItemSize={200}
                numColumns={4}
                contentContainerStyle={{
                    flexGrow: 1,
                }}
                columnWrapperStyle={{ justifyContent: "space-between" }}
                scrollEnabled={false}
                type={ListView.Type.Flash}
                showsVerticalScrollIndicator={false}
                keyExtractor={(item) => `${item?.productId?.toString()}_product`}
                renderItem={({ item }) => (
                    <ProductCard deleteFav={() => getFavorites(false)} isFavorites product={item} />
                )}
            />
        );
    }, [favList, isLoading, error]);

    return (
        <ParentScrollView>
            <View style={styles.container}>
                <VText style={styles.header}>Favorites</VText>
                <View style={styles.content}>
                    <SideProfileComp gender={gender} selected="Favorites" />
                    <View style={styles.formSection}>{renderContent()}</View>
                </View>
            </View>
        </ParentScrollView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: Colors.white,
        paddingTop: spV(32),
    },
    listView: {
        flex: 1,
    },
    emptyContainer: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
    },
    emptyImage: {
        width: wp(60),
        height: wp(60),
    },
    emptyButton: {
        height: hp(50),
        width: wp(230),
        marginTop: spV(15),
    },
    errorText: {
        fontFamily: "JosefinSans",
        fontSize: fp(18),
        textAlign: "center",
        marginTop: spV(20),
        color: Colors.red,
    },
    loadingContainer: {
        justifyContent: "center",
        alignItems: "center",
    },
    header: {
        textAlign: "center",
        fontSize: fp(28),
        fontFamily: "LibreBodoni",
        fontWeight: "350",
        marginBottom: spV(28),
    },
    loadingContainer: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
    },
    emptyText: {
        fontFamily: "JosefinSans",
        fontSize: fp(20),
        fontWeight: "550",
        textAlign: "center",
        marginTop: spV(15),
    },
    emptySubText: {
        fontFamily: "JosefinSans",
        fontSize: fp(16),
        fontWeight: "350",
        textAlign: "center",
        marginTop: spV(5),
    },
    productContainer: {
        width: wp(285),
        marginBottom: spV(20),
    },
    columnWrapper: {
        justifyContent: "space-between",
    },
    removeButton: {
        backgroundColor: Colors.black,
        padding: spV(10),
        alignItems: "center",
        marginTop: spV(10),
    },
    removeButtonText: {
        color: Colors.white,
        fontFamily: "JosefinSans",
        fontSize: fp(14),
    },
    content: {
        flex: 1,
        flexDirection: "row",
    },
    menuSection: {
        marginLeft: spH(70),
        width: "20%",
        paddingRight: spH(20),
        borderRightWidth: 1,
        borderRightColor: "#eee",
    },
    menuItem: {
        flexDirection: "row",
        alignItems: "center",
        height: spV(65),
        paddingRight: spH(16),
        borderBottomWidth: 1,
        borderBottomColor: "#eee",
    },
    formSection: {
        width: wp(920),
        marginHorizontal: spH(80),
    },
});

export default Favorites;
